/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress } from '@mui/material';
import React from 'react';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { AlertBanner } from '@opengeoweb/shared';
import MetInfoWrapper from './MetInfoWrapper';
import { useProductConfig } from '../../api/hooks';
import type { ProductType } from '../../types';
import { useSigmetAirmetTranslation } from '../../utils/i18n';

interface ModuleWrapperProps {
  productType: ProductType;
}

const DownloadSigmetAirmetConfigWrapper: React.FC<ModuleWrapperProps> = ({
  productType,
}) => {
  const { t } = useSigmetAirmetTranslation();
  const { isLoggedIn } = useAuthenticationContext();

  const {
    data: productConfig,
    isLoading,
    error,
  } = useProductConfig(productType);

  if (isLoading) {
    return <LinearProgress data-testid="loadingSpinner" />;
  }

  if (error) {
    return (
      <AlertBanner
        info={!isLoggedIn ? t('error-auth-login') : error.message}
        title={t('error-title')}
      />
    );
  }

  if (!productConfig) {
    return (
      <AlertBanner info={t('error-no-config-found')} title={t('error-title')} />
    );
  }

  return (
    <MetInfoWrapper productConfig={productConfig} productType={productType} />
  );
};

export default DownloadSigmetAirmetConfigWrapper;
